import {
	InfoCircleOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Col, Row, Checkbox, Tooltip } from 'antd';
import { postApi, putApi } from 'apis';
import Buttons from 'components/Global/Buttons';
import FormulaInput from 'components/Global/FormulaField';
import InputField from 'components/Global/InputField';
import InputNumberField from 'components/Global/InputNumberField';
import { earningsFormulaMapping } from 'constants/Data';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	allowedVariablesForEarningsFormula,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
	validateFormula,
} from 'utils/utils';
import { fetchCompanyWizard } from '../../../../../redux/actions/payrollSettingWizardAction';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';

const AddDeductionBody: FC<SideDrawerBodyProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const {
		closeDrawerByAnimation,
		fetchDeductions,
		editSelected,
		isLoanEditable,
	} = props;

	const dispatch = useDispatch<AppDispatch>();
	const { baseCurrencySymbol } = useSelector(
		(state: any) => state.userProfile.data
	);

	const [deductionsDetails, setDeductionsDetails] = useState<any>({
		displayName: '',
		formula: null,
		amount: null,
		isThirdPartyLoanDeduction: false,
	});

	const [formError, setFormError] = useState<any>({
		displayName: false,
		formula: false,
		mutualExclusion: null,
	});

	const buttons = [
		{
			text: `Cancel`,
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: loading,
			onclick: () => {
				closeDrawerByAnimation();
			},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: `Save`,
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: () => {
				handleSubmit();
			},
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	const handleChange = (
		value: string | number | null | string[] | boolean,
		name: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		OnChange(value, name);
	};

	const OnChange = (
		value: string | number | null | string[] | boolean,
		key: string
	) => {
		// Skip amount/formula validation if Third Party Loan Deduction is checked
		if (key === 'isThirdPartyLoanDeduction') {
			const checked = value === true;

			setDeductionsDetails((prev: any) => ({
				...prev,
				isThirdPartyLoanDeduction: checked,
				amount: checked ? null : prev.amount,
				formula: checked ? null : prev.formula,
			}));

			setFormError((prev: any) => ({
				...prev,
				mutualExclusion: null,
				formula: false,
			}));

			return;
		}

		setDeductionsDetails((prev: any) => {
			return {
				...prev,
				[key]: value,
			};
		});

		// Skip formula validation for checkbox changes
		if (key === 'isThirdPartyLoanDeduction') {
			return;
		}

		const updatedAmount =
			key === 'amount' ? value : deductionsDetails.amount;
		const updatedFormula =
			key === 'formula' ? value : deductionsDetails.formula;

		const isAmountValid =
			updatedAmount !== null && Number(updatedAmount) !== 0;
		const isFormulaValid =
			typeof updatedFormula === 'string' &&
			updatedFormula.trim() !== '' &&
			validateFormula(updatedFormula, allowedVariablesForEarningsFormula);

		let mutualExclusionError = null;

		if (isAmountValid && isFormulaValid) {
			mutualExclusionError =
				'Only one of amount or formula can be entered.';
		} else if (!isAmountValid && !isFormulaValid) {
			mutualExclusionError = 'Amount or formula is required.';
		}

		const updatedFormError = {
			...formError,
			mutualExclusion: mutualExclusionError,
		};

		const checkFormError = validateFormData(
			{ [key]: value },
			updatedFormError
		);
		setFormError(checkFormError);
	};

	const handleSubmit = async () => {
		let checkFormError: any = validateFormData(
			{ ...deductionsDetails },
			{ ...formError }
		);

		if (!deductionsDetails.isThirdPartyLoanDeduction) {
			const isFormulaValid =
				deductionsDetails?.formula &&
				validateFormula(
					deductionsDetails.formula as string,
					allowedVariablesForEarningsFormula
				);

			checkFormError = {
				...checkFormError,
				formula: deductionsDetails?.formula ? !isFormulaValid : false,
			};

			const isAmountValid =
				deductionsDetails.amount !== null &&
				Number(deductionsDetails.amount) !== 0;

			// 🔑 Same validation as OnChange
			if (isAmountValid && isFormulaValid) {
				checkFormError = {
					...checkFormError,
					mutualExclusion:
						'Only one of amount or formula can be entered.',
				};
			} else if (!isAmountValid && !isFormulaValid) {
				checkFormError = {
					...checkFormError,
					mutualExclusion: 'Amount or formula is required.',
				};
			}
		} else {
			// ✅ Skip amount/formula validation when Third Party Loan is selected
			checkFormError = {
				...checkFormError,
				formula: false,
				mutualExclusion: null,
			};
		}

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) return;

		// 🔑 Always nullify amount & formula for third-party loan before saving
		let payloadAmount;
		if (deductionsDetails.isThirdPartyLoanDeduction) {
			payloadAmount = null;
		} else if (deductionsDetails.formula) {
			payloadAmount = null;
		} else {
			payloadAmount = deductionsDetails.amount;
		}

		const payload = {
			...deductionsDetails,
			amount: payloadAmount,
			formula: deductionsDetails.isThirdPartyLoanDeduction
				? null
				: deductionsDetails.formula,
		};

		if (editSelected) {
			await updateDeduction({
				...payload,
				deductionCodeId: editSelected?.id,
			});
		} else {
			await addDeduction(payload);
			dispatch(fetchCompanyWizard());
		}
	};

	const updateDeduction = async (deductionData: any) => {
		try {
			setLoading(true);
			const response = await putApi('/deduction', deductionData);
			toastText(
				response?.data?.message ||
					'Deduction details saved successfully',
				'success'
			);
			dispatch(fetchCompanyWizard());
			fetchDeductions();
			closeDrawerByAnimation();
		} catch (err: any) {
			toastText(
				err?.response?.data?.message ||
					'Something went wrong in updating deduction',
				'error'
			);
		} finally {
			setLoading(false);
		}
	};

	const addDeduction = async (finalData: any) => {
		try {
			setLoading(true);
			const response = await postApi('/deduction', finalData);
			toastText(
				response?.data?.message || 'Deduction created successfully',
				'success'
			);
			fetchDeductions();
			closeDrawerByAnimation();
		} catch (err: any) {
			toastText(
				err?.response?.data?.message ||
					'Something went wrong when creating deduction',
				'error'
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (editSelected) {
			setDeductionsDetails({ ...editSelected });
		}
	}, [editSelected]);

	return (
		<>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['side-drawer-body']}>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputField
							name="displayName"
							value={deductionsDetails.displayName}
							label="Display Name"
							disabled={editSelected}
							required={true}
							helperText="Please enter Display Name"
							onChange={(value) => {
								handleChange(value, 'displayName', true);
							}}
							isError={formError.displayName}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputNumberField
							name="amount"
							value={deductionsDetails.amount}
							label="Amount"
							suffix={baseCurrencySymbol}
							required={false}
							helperText={
								formError.mutualExclusion ||
								(formError.amount ? 'Amount is required' : '')
							}
							onChange={(value) => {
								handleChange(value, 'amount', false);
							}}
							isError={!!formError.mutualExclusion}
							disabled={
								!!deductionsDetails.formula ||
								deductionsDetails.isThirdPartyLoanDeduction
							}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<FormulaInput
							name="formula"
							value={deductionsDetails?.formula}
							placeholder={'SAL*OTH*1.5'}
							label="Formula"
							required={false}
							helperText={
								formError.mutualExclusion ||
								(formError.formula
									? 'Enter a valid formula'
									: '')
							}
							onChange={(value) => {
								handleChange(value, 'formula', false);
							}}
							isError={!!formError.mutualExclusion}
							disabled={
								!!deductionsDetails.amount ||
								deductionsDetails.isThirdPartyLoanDeduction
							}
							labelSuffix={<InfoCircleOutlined />}
							labelSuffixContent={
								<div>
									<p
										style={{
											fontWeight: 500,
											fontSize: '18px',
										}}
									>
										Formula Variables
									</p>
									<ul>
										{Object.entries(
											earningsFormulaMapping
										).map(([key, value]) => (
											<ul key={key}>
												<strong>{key}:</strong> {value}
											</ul>
										))}
									</ul>
								</div>
							}
						/>
					</Col>
				</Row>

				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							}}
						>
							<Checkbox
								checked={
									deductionsDetails.isThirdPartyLoanDeduction
								}
								disabled={!isLoanEditable}
								onChange={(e) => {
									handleChange(
										e.target.checked,
										'isThirdPartyLoanDeduction',
										false
									);
								}}
							>
								<span
									style={{
										color: 'black',
									}}
								>
									Third Party Loan Deduction
								</span>
							</Checkbox>
							{!isLoanEditable && (
								<Tooltip title="Cannot change the Loan Configuration due to pending or draft payroll">
									<ExclamationCircleOutlined
										style={{
											color: '#584495',
											fontSize: '16px',
											cursor: 'pointer',
										}}
									/>
								</Tooltip>
							)}
						</div>
					</Col>
				</Row>

				<div className={styles['buttons']}>
					<Buttons buttons={buttons} />
				</div>
			</div>
		</>
	);
};

export default AddDeductionBody;
